/**
 * Pure mapping between an `<fp-text>` block's MARKDOWN body and the TipTap
 * editor document used during an inline text-edit session.
 *
 * An `fp-text` body IS markdown, so loading parses that markdown into a small
 * typed editor-doc model (paragraphs, headings, lists, dividers,
 * bold/italic/underline runs, hard breaks and `{{ source }}` field chips) and
 * committing serializes the edited doc back to a markdown string.
 *
 * Parsing runs the SAME pipeline the renderer uses - react-markdown with
 * remark-gfm, `remarkFieldTokens` and `remarkHardBreaks` - so the session shows
 * exactly what `FpText` renders and a round trip is stable by construction.
 *
 * Knowingly lossy, because the vocabulary has no counterpart: links, images,
 * inline code, fenced code and GFM tables all flatten to their text. Three
 * constructs are the exception, each carrying what markdown has no syntax of
 * its own for: a blockquote is one indent level, `<u>` inline HTML is an
 * underline, and a `<span size="..." color="...">` is one run's own size and
 * colour. Bold and italic fall back to `<b>`/`<i>` inline HTML for the one
 * run markdown cannot express - a punctuation-edged run flush against text,
 * where CommonMark's flanking rules read the asterisks as literals.
 */

import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import {
  INLINE_TEXT_SPAN_CLOSE_TAG,
  INLINE_TEXT_SPAN_COLOR_PROPERTY,
  INLINE_TEXT_SPAN_SIZE_PROPERTY,
  inlineTextSpanTag,
  readInlineTextSpanAttributes,
  type InlineTextSpanAttributes,
} from "@/components/inlineTextSpan";
import { MarkdownReact } from "@/components/MarkdownReact";
import { dedentAuthoredBody } from "./FpText";
import { quoteFieldLabel } from "./quoteFields";
import { remarkFieldTokens } from "./remarkFieldTokens";
import { remarkHardBreaks } from "./remarkHardBreaks";

/** TipTap node name of the atomic fp-field chip. */
export const FIELD_CHIP_NODE_NAME = "fieldChip";

/** TipTap node name of one indent level, stored as a markdown blockquote. */
export const INDENT_NODE_NAME = "blockquote";

/** TipTap mark name of a run's own size and colour, stored as a `<span>`. */
export const TEXT_STYLE_MARK_NAME = "textStyle";

/** The editor document for one inline text-edit session. */
export interface EditorDoc {
  type: "doc";
  content: EditorBlockNode[];
}

/** A top-level editor block. */
export type EditorBlockNode =
  | EditorParagraph
  | EditorHeading
  | EditorHorizontalRule
  | EditorList
  | EditorIndent;

/** A paragraph block. */
export interface EditorParagraph {
  type: "paragraph";
  content?: EditorInlineNode[];
}

/**
 * The heading levels a markdown heading ("# " to "#### ") can reach. It stops
 * at four because the prose stylesheet the document renders through styles h1
 * to h4 and nothing deeper: an h5 would fall back to the browser's default and
 * print smaller than the body text around it.
 */
export const HEADING_LEVELS = [1, 2, 3, 4] as const;

/** One of the heading levels the session offers. */
export type HeadingLevel = (typeof HEADING_LEVELS)[number];

/** A heading block, serialized as "#" repeated per level. */
export interface EditorHeading {
  type: "heading";
  attrs: { level: HeadingLevel };
  content?: EditorInlineNode[];
}

/** A divider block, serialized as "---". */
export interface EditorHorizontalRule {
  type: "horizontalRule";
}

/** A bullet or numbered list block. */
export interface EditorList {
  type: "bulletList" | "orderedList";
  attrs?: { tight?: boolean; start?: number };
  content: EditorListItem[];
}

/**
 * One indent level, holding the blocks sitting at it. Markdown has no indent of
 * its own, so this is stored as a blockquote - the one container it can nest to
 * any depth - and rendered as a plain indent rather than as a quotation.
 */
export interface EditorIndent {
  type: "blockquote";
  content: EditorBlockNode[];
}

/** One list item, holding paragraph and nested-list blocks. */
export interface EditorListItem {
  type: "listItem";
  content: EditorBlockNode[];
}

/** An inline editor node. */
export type EditorInlineNode = EditorText | EditorHardBreak | EditorFieldChip;

/** A text run with optional formatting marks. */
export interface EditorText {
  type: "text";
  text: string;
  marks?: EditorMark[];
}

/** A formatting mark on a text run. */
export type EditorMark = EditorEmphasisMark | EditorTextStyleMark;

/** An attribute-free emphasis mark. */
export interface EditorEmphasisMark {
  type: "bold" | "italic" | "underline";
}

/** A run's own size and/or colour. */
export interface EditorTextStyleMark {
  type: typeof TEXT_STYLE_MARK_NAME;
  attrs: InlineTextSpanAttributes;
}

/** A hard line break, serialized as a single newline. */
export interface EditorHardBreak {
  type: "hardBreak";
}

/** An atomic `{{ source }}` field chip. */
export interface EditorFieldChip {
  type: typeof FIELD_CHIP_NODE_NAME;
  attrs: {
    /** The field source key ("" when the token names none). */
    source: string;
    /** The resolved display text shown inside the editor pill. */
    label: string;
  };
  marks?: EditorMark[];
}

/** Resolves a field `source` key to its display value, or null. */
export type FieldResolver = (source: string) => string | null;

/** Tags whose inline content maps to a formatting mark. */
const MARK_TAGS: Record<string, EditorEmphasisMark["type"]> = {
  strong: "bold",
  b: "bold",
  em: "italic",
  i: "italic",
  u: "underline",
};

/** Rendered tags that break an inline run, so a paragraph must flush first. */
const BLOCK_TAGS = new Set([
  "blockquote",
  "div",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "hr",
  "li",
  "ol",
  "p",
  "pre",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "ul",
]);

/** Every `{{ source }}` token becomes an fp-field element for the walker. */
const fieldTokenElements = remarkFieldTokens((source) => ({
  fieldSource: source,
}));

/**
 * The body of an empty paragraph. Markdown has no way to store a blank line -
 * the gap between two paragraphs is already its separator - so a non-breaking
 * space holds the line open, the same trick a tab stop uses.
 */
export const BLANK_LINE = "\u00A0";

/** Inline punctuation backslash-escaped so authored text survives a reparse. */
const INLINE_PUNCTUATION = /[\\`*_[\]<>&]/g;

/** A line-leading bullet, plus or heading marker in otherwise plain text. */
const LEADING_MARKER = /^([ \t]*)([-+#])/gm;

/** A line-leading ordered-list marker in otherwise plain text. */
const LEADING_NUMBER = /^([ \t]*)(\d+)([.)])/gm;

/**
 * The pill label for a field chip: the resolved value, or a friendly
 * placeholder when the field is empty or unresolvable.
 * @param source - The field source key ("" when absent)
 * @param value - The resolved field value, or null when unresolvable
 * @returns The chip display text
 */
export function fieldChipLabel(source: string, value: string | null): string {
  return value !== null && value !== ""
    ? value
    : `⟨${quoteFieldLabel(source !== "" ? source : null) ?? "Empty field"}⟩`;
}

/**
 * Parses an authored element's markdown body into the session's editor doc.
 * @param source - The source element entering the edit session
 * @param resolveField - Resolves field sources to chip display values
 * @returns The editor document seeding the TipTap session
 */
export function loadEditorDocFromSource(
  source: Element,
  resolveField: FieldResolver,
): EditorDoc {
  return markdownToEditorDoc(authoredSourceMarkdown(source), resolveField);
}

/**
 * The dedented markdown an authored element's whole-body session edits.
 * @param source - The source element entering the edit session
 * @returns The element's body as dedented markdown
 */
export function authoredSourceMarkdown(source: Element): string {
  return dedentAuthoredBody(authoredMarkdown(source));
}

/**
 * The markdown behind an authored element's body: its text, with any nested
 * `<fp-field>` child written as its inline `{{ source }}` token - so a cell
 * holding a field element seeds the same session its committed token form
 * would, and the field survives the edit as a chip.
 * @param source - The source element entering the edit session
 * @returns The element's body as markdown
 */
function authoredMarkdown(source: Element): string {
  return Array.from(source.childNodes)
    .map((node) =>
      node instanceof Element && node.tagName.toLowerCase() === "fp-field"
        ? `{{ ${node.getAttribute("source") ?? ""} }}`
        : (node.textContent ?? ""),
    )
    .join("");
}

/**
 * Parses a markdown string into the session's editor document.
 * @param markdown - The markdown body
 * @param resolveField - Resolves field sources to chip display values
 * @returns The editor document, never empty
 */
export function markdownToEditorDoc(
  markdown: string,
  resolveField: FieldResolver,
): EditorDoc {
  const html = renderToStaticMarkup(
    createElement(MarkdownReact, {
      extraRemarkPlugins: [fieldTokenElements, remarkHardBreaks],
      children: markdown,
    }),
  );
  const rendered = new DOMParser().parseFromString(html, "text/html");
  const content = blocksFromNodes(
    Array.from(rendered.body.childNodes),
    resolveField,
  );
  return {
    type: "doc",
    content: content.length > 0 ? content : [{ type: "paragraph" }],
  };
}

/**
 * Serializes a committed editor document back to the markdown an `<fp-text>`
 * body stores, a body of nothing but blank lines storing as empty.
 * @param doc - The committed editor document
 * @returns The markdown body
 */
export function editorDocToMarkdown(doc: EditorDoc): string {
  const markdown = editorDocToSliceMarkdown(doc);
  return markdown.trim() === "" ? "" : markdown;
}

/**
 * Serializes a session doc showing one slice of a larger body. Its blank lines
 * are lines of that body, so they are all kept: only the editor's emptied
 * state - one lone empty paragraph - serializes to nothing.
 * @param doc - The session's editor document
 * @returns The slice's markdown
 */
export function editorDocToSliceMarkdown(doc: EditorDoc): string {
  const [first] = doc.content;
  if (
    doc.content.length === 1 &&
    first.type === "paragraph" &&
    (first.content ?? []).length === 0
  ) {
    return "";
  }
  return doc.content.map(blockToMarkdown).join("\n\n");
}

/**
 * Narrows an editor's JSON output (editor.getJSON()) to the typed editor-doc
 * model, rejecting anything outside the session schema.
 * @param json - The raw JSON document from the TipTap editor
 * @returns The typed editor doc, or null when the JSON doesn't conform
 */
export function parseEditorDocJson(json: unknown): EditorDoc | null {
  if (!isRecord(json) || json.type !== "doc") {
    return null;
  }
  const content = collectAll(asArray(json.content), parseBlockJson);
  return content ? { type: "doc", content } : null;
}

/**
 * Maps rendered child nodes to editor blocks, collecting loose inline runs
 * into paragraphs so mixed content (a list item's text beside a nested list)
 * lands in document order.
 * @param nodes - The rendered child nodes
 * @param resolveField - Resolves field sources to chip display values
 * @returns The editor blocks, in order
 */
function blocksFromNodes(
  nodes: ChildNode[],
  resolveField: FieldResolver,
): EditorBlockNode[] {
  const { blocks, inline } = nodes.reduce<{
    blocks: EditorBlockNode[];
    inline: ChildNode[];
  }>(
    (acc, node) => {
      if (
        !(node instanceof Element) ||
        !BLOCK_TAGS.has(node.tagName.toLowerCase())
      ) {
        return { ...acc, inline: [...acc.inline, node] };
      }
      return {
        blocks: [
          ...acc.blocks,
          ...paragraphFromNodes(acc.inline, resolveField),
          ...blockFromElement(node, resolveField),
        ],
        inline: [],
      };
    },
    { blocks: [], inline: [] },
  );
  return [...blocks, ...paragraphFromNodes(inline, resolveField)];
}

/**
 * Wraps a buffered inline run in a paragraph, dropping runs that carry only
 * whitespace (the gaps the renderer writes between block elements).
 * @param nodes - The buffered inline nodes
 * @param resolveField - Resolves field sources to chip display values
 * @returns The paragraph block, or nothing
 */
function paragraphFromNodes(
  nodes: ChildNode[],
  resolveField: FieldResolver,
): EditorBlockNode[] {
  const content = inlineContent(nodes, resolveField);
  if (content.length === 0) {
    return [];
  }
  return isBlankLine(content)
    ? [{ type: "paragraph" }]
    : [{ type: "paragraph", content }];
}

/**
 * Whether a paragraph holds nothing but the non-breaking space a committed
 * blank line is stored as, so the session shows it as the empty line it is
 * instead of a line the caret has to type past.
 * @param content - The paragraph's inline content
 * @returns True when the paragraph is a stored blank line
 */
function isBlankLine(content: EditorInlineNode[]): boolean {
  const [node] = content;
  return (
    content.length === 1 && node.type === "text" && node.text === BLANK_LINE
  );
}

/**
 * Maps a block's children to its inline content, trimming the whitespace the
 * renderer writes at the edges of a block so it never becomes a text run. A
 * non-breaking space is left alone: that one is authored indentation.
 * @param nodes - The block's child nodes
 * @param resolveField - Resolves field sources to chip display values
 * @returns The inline content, empty when the block holds only whitespace
 */
function inlineContent(
  nodes: ChildNode[],
  resolveField: FieldResolver,
): EditorInlineNode[] {
  const content = inlineFromNodes(nodes, [], resolveField);
  const trimmed = content
    .map((node, index): EditorInlineNode => {
      if (node.type !== "text") {
        return node;
      }
      const lead =
        index === 0 ? node.text.replace(/^[ \t\n\r\f]+/, "") : node.text;
      return {
        ...node,
        text:
          index === content.length - 1
            ? lead.replace(/[ \t\n\r\f]+$/, "")
            : lead,
      };
    })
    .filter((node) => node.type !== "text" || node.text !== "");
  return dropBlankLineRuns(trimmed);
}

/**
 * Drops the stored blank line holding open each empty line between hard
 * breaks, so the session shows the empty line it stands for instead of a
 * character the caret has to type past (textToMarkdown writes them).
 * @param content - The block's inline content
 * @returns The content with its empty lines' placeholders dropped
 */
function dropBlankLineRuns(content: EditorInlineNode[]): EditorInlineNode[] {
  if (!content.some((node) => node.type === "hardBreak")) {
    return content;
  }
  return content.filter(
    (node, index) =>
      node.type !== "text" ||
      node.text !== BLANK_LINE ||
      !isLineEdge(content[index - 1]) ||
      !isLineEdge(content[index + 1]),
  );
}

/**
 * Whether an inline neighbor ends a line: a hard break or the block's edge.
 * @param node - The neighboring inline node, or undefined at the block edge
 * @returns True when nothing else shares the line on that side
 */
function isLineEdge(node: EditorInlineNode | undefined): boolean {
  return node === undefined || node.type === "hardBreak";
}

/**
 * Maps one rendered block element to its editor blocks. A construct the
 * session schema has no node for (a table, a code block) flattens to the
 * blocks of its children.
 * @param element - The rendered block element
 * @param resolveField - Resolves field sources to chip display values
 * @returns The editor blocks, in order
 */
function blockFromElement(
  element: Element,
  resolveField: FieldResolver,
): EditorBlockNode[] {
  const tag = element.tagName.toLowerCase();
  const children = Array.from(element.childNodes);
  if (tag === "p") {
    return paragraphFromNodes(children, resolveField);
  }
  if (tag === "hr") {
    return [{ type: "horizontalRule" }];
  }
  const level = headingLevelOf(tag);
  if (level !== null) {
    const content = inlineContent(children, resolveField);
    return [
      content.length > 0
        ? { type: "heading", attrs: { level }, content }
        : { type: "heading", attrs: { level } },
    ];
  }
  if (tag === "ul" || tag === "ol") {
    return [
      {
        type: tag === "ul" ? "bulletList" : "orderedList",
        ...listAttrsFromElement(element, tag),
        content: listItemsFromElement(element, resolveField),
      },
    ];
  }
  if (tag === "blockquote") {
    const content = blocksFromNodes(children, resolveField);
    return content.length > 0 ? [{ type: "blockquote", content }] : [];
  }
  return blocksFromNodes(children, resolveField);
}

/**
 * The list attrs a rendered list carries: `tight: false` for a loose list (its
 * items wrap their text in paragraphs) and an ordered list's `start` number.
 * @param element - The rendered `<ul>` or `<ol>`
 * @param tag - The element's lowercased tag name
 * @returns `{ attrs }` when either applies, otherwise an empty object
 */
function listAttrsFromElement(
  element: Element,
  tag: string,
): Pick<EditorList, "attrs"> {
  const loose = element.querySelector(":scope > li > p") !== null;
  const start = tag === "ol" ? element.getAttribute("start") : null;
  if (!loose && start === null) {
    return {};
  }
  return {
    attrs: {
      ...(loose ? { tight: false } : {}),
      ...(start !== null ? { start: Number(start) } : {}),
    },
  };
}

/**
 * The offered heading level for a rendered heading tag, clamped to the levels
 * the session schema carries.
 * @param tag - The lowercased tag name
 * @returns The heading level, or null when the tag is not a heading
 */
function headingLevelOf(tag: string): HeadingLevel | null {
  const match = /^h([1-6])$/.exec(tag);
  if (!match) {
    return null;
  }
  const level = Number(match[1]);
  return isHeadingLevel(level)
    ? level
    : HEADING_LEVELS[HEADING_LEVELS.length - 1];
}

/**
 * Narrows a value to one of the heading levels the session offers.
 * @param value - The candidate level
 * @returns True when the value is an offered heading level
 */
function isHeadingLevel(value: unknown): value is HeadingLevel {
  return HEADING_LEVELS.some((level) => level === value);
}

/**
 * Maps a rendered list's `<li>` children to editor list items.
 * @param list - The rendered `<ul>`/`<ol>` element
 * @param resolveField - Resolves field sources to chip display values
 * @returns The list items, each holding at least one block
 */
function listItemsFromElement(
  list: Element,
  resolveField: FieldResolver,
): EditorListItem[] {
  return Array.from(list.children)
    .filter((child) => child.tagName.toLowerCase() === "li")
    .map((item): EditorListItem => {
      const content = blocksFromNodes(
        Array.from(item.childNodes),
        resolveField,
      );
      return {
        type: "listItem",
        content: content.length > 0 ? content : [{ type: "paragraph" }],
      };
    });
}

/**
 * Maps rendered inline nodes to editor inline nodes, carrying the active marks
 * into nested mark tags.
 * @param nodes - The rendered nodes to map
 * @param marks - The formatting marks active at this nesting level
 * @param resolveField - Resolves field sources to chip display values
 * @returns The editor inline nodes, in order
 */
function inlineFromNodes(
  nodes: ChildNode[],
  marks: EditorMark[],
  resolveField: FieldResolver,
): EditorInlineNode[] {
  return nodes.flatMap((node): EditorInlineNode[] => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = textRunOf(node);
      if (text === "") {
        return [];
      }
      return [
        marks.length > 0
          ? { type: "text", text, marks }
          : { type: "text", text },
      ];
    }
    if (!(node instanceof Element)) {
      return [];
    }
    const tag = node.tagName.toLowerCase();
    if (tag === "br") {
      return [{ type: "hardBreak" }];
    }
    if (tag === "fp-field") {
      return [fieldChipFromElement(node, marks, resolveField)];
    }
    if (tag === "img" || tag === "input") {
      return [];
    }
    const mark = tag === "span" ? textStyleMarkOf(node) : emphasisMarkOf(tag);
    return inlineFromNodes(
      Array.from(node.childNodes),
      mark === null ? marks : addMark(marks, mark),
      resolveField,
    );
  });
}

/**
 * The emphasis mark a rendered tag stands for.
 * @param tag - The lowercased tag name
 * @returns The mark, or null when the tag carries no formatting
 */
function emphasisMarkOf(tag: string): EditorEmphasisMark | null {
  const type = MARK_TAGS[tag];
  return type === undefined ? null : { type };
}

/**
 * The styling mark a rendered `<span>` stands for, read off the authored
 * values the renderer carried onto it.
 * @param element - The rendered span element
 * @returns The mark, or null when the span carries no valid appearance
 */
function textStyleMarkOf(element: Element): EditorTextStyleMark | null {
  const attrs = readInlineTextSpanAttributes({
    size: element.getAttribute(INLINE_TEXT_SPAN_SIZE_PROPERTY),
    color: element.getAttribute(INLINE_TEXT_SPAN_COLOR_PROPERTY),
  });
  return attrs === null ? null : { type: TEXT_STYLE_MARK_NAME, attrs };
}

/**
 * Reads a rendered text node's content, dropping the formatting newline the
 * markdown renderer writes immediately after every `<br>`.
 * @param node - The rendered text node
 * @returns The text run's content
 */
function textRunOf(node: ChildNode): string {
  const text = node.nodeValue ?? "";
  const previous = node.previousSibling;
  return previous instanceof Element && previous.tagName.toLowerCase() === "br"
    ? text.replace(/^\n/, "")
    : text;
}

/**
 * Builds a chip from a rendered `<fp-field>` element.
 * @param element - The rendered fp-field element
 * @param marks - The formatting marks active around the element
 * @param resolveField - Resolves field sources to chip display values
 * @returns The chip node
 */
function fieldChipFromElement(
  element: Element,
  marks: EditorMark[],
  resolveField: FieldResolver,
): EditorFieldChip {
  const source = element.getAttribute("source") ?? "";
  const attrs = { source, label: fieldChipLabel(source, resolveField(source)) };
  return marks.length > 0
    ? { type: FIELD_CHIP_NODE_NAME, attrs, marks }
    : { type: FIELD_CHIP_NODE_NAME, attrs };
}

/**
 * Appends a mark to the active set without duplicating it, the innermost tag
 * winning when the same mark nests inside itself.
 * @param marks - The currently active marks
 * @param mark - The mark introduced by a nested tag
 * @returns The combined mark set
 */
function addMark(marks: EditorMark[], mark: EditorMark): EditorMark[] {
  return marks.some((existing) => existing.type === mark.type)
    ? marks.map((existing) => (existing.type === mark.type ? mark : existing))
    : [...marks, mark];
}

/**
 * Serializes one committed block to markdown.
 * @param block - The committed block
 * @returns The markdown for the block, without its trailing blank line
 */
function blockToMarkdown(block: EditorBlockNode): string {
  if (block.type === "horizontalRule") {
    return "---";
  }
  if (block.type === "paragraph") {
    return textToMarkdown(block.content ?? []) || BLANK_LINE;
  }
  if (block.type === "heading") {
    return `${"#".repeat(block.attrs.level)} ${textToMarkdown(block.content ?? [])}`;
  }
  if (block.type === "blockquote") {
    return indentToMarkdown(block);
  }
  return listToMarkdown(block);
}

/**
 * Serializes one indent level, prefixing every line of the blocks inside it. A
 * blank line keeps the bare marker, so the whole run stays one block instead of
 * splitting into two at the gap.
 * @param indent - The committed indent block
 * @returns The markdown for the indented run
 */
function indentToMarkdown(indent: EditorIndent): string {
  return indent.content
    .map(blockToMarkdown)
    .join("\n\n")
    .split("\n")
    .map((line) => (line === "" ? ">" : `> ${line}`))
    .join("\n");
}

/** Whether a run's neighbors touch it with no whitespace between. */
interface FlushEdges {
  before: boolean;
  after: boolean;
}

/**
 * Serializes a block's inline content, escaping anything that would otherwise
 * reparse as markup. Each node learns whether its neighbors touch it flush,
 * which decides how its emphasis marks can be written.
 * @param content - The committed inline nodes
 * @returns The markdown text
 */
function textToMarkdown(content: EditorInlineNode[]): string {
  const text = content
    .map((node, index) =>
      inlineNodeToMarkdown(node, {
        before: touchesFlush(content[index - 1], "end"),
        after: touchesFlush(content[index + 1], "start"),
      }),
    )
    .join("");
  return holdEmptyLinesOpen(text, content)
    .replace(LEADING_MARKER, "$1\\$2")
    .replace(LEADING_NUMBER, "$1$2\\$3");
}

/**
 * Writes a stored blank line on every empty line of a block broken by hard
 * breaks. Markdown drops an empty line - two newlines end the paragraph, and
 * a trailing one is ignored - so without it a Shift+Enter line left empty
 * would vanish from the render and from the stored body.
 * @param text - The block's serialized inline text
 * @param content - The block's inline content
 * @returns The text with each empty line held open
 */
function holdEmptyLinesOpen(text: string, content: EditorInlineNode[]): string {
  if (!content.some((node) => node.type === "hardBreak")) {
    return text;
  }
  return text
    .split("\n")
    .map((line) => (line === "" ? BLANK_LINE : line))
    .join("\n");
}

/**
 * Whether a neighboring node presses a non-whitespace character against the
 * run being serialized. A missing neighbor or a hard break is open space; a
 * chip's token braces always touch.
 * @param node - The neighboring inline node, or undefined at the block edge
 * @param edge - Which edge of the neighbor faces the run
 * @returns True when the neighbor touches with no whitespace between
 */
function touchesFlush(
  node: EditorInlineNode | undefined,
  edge: "start" | "end",
): boolean {
  if (!node || node.type === "hardBreak") {
    return false;
  }
  if (node.type === FIELD_CHIP_NODE_NAME) {
    return true;
  }
  const char = edge === "start" ? node.text.slice(0, 1) : node.text.slice(-1);
  return char !== "" && !/\s/.test(char);
}

/**
 * Serializes one committed inline node.
 * @param node - The committed inline node
 * @param flush - Whether the node's neighbors touch it with no whitespace
 * @returns The markdown for the node
 */
function inlineNodeToMarkdown(
  node: EditorInlineNode,
  flush: FlushEdges,
): string {
  if (node.type === "hardBreak") {
    return "\n";
  }
  if (node.type === FIELD_CHIP_NODE_NAME) {
    return emphasize(`{{ ${node.attrs.source} }}`, node.marks ?? [], flush);
  }
  return emphasize(
    node.text.replace(INLINE_PUNCTUATION, "\\$&"),
    node.marks ?? [],
    flush,
  );
}

/** A run inside a `<span>` is never flush: the tag's own punctuation parts it. */
const SPANNED_EDGES: FlushEdges = { before: false, after: false };

/**
 * Wraps a text run in the markup its marks call for: the styling `<span>`
 * outermost, then the emphasis delimiters. The run's outer whitespace stays
 * outside both, so the same run serializes identically however it is split.
 * @param text - The already-escaped text run
 * @param marks - The formatting marks on the run
 * @param flush - Whether the run's neighbors touch it with no whitespace
 * @returns The marked-up markdown
 */
function emphasize(
  text: string,
  marks: EditorMark[],
  flush: FlushEdges,
): string {
  const attributes = textStyleAttributesOf(marks);
  if (attributes === null) {
    return emphasizeRun(text, marks, flush);
  }
  const trimmed = text.trim();
  if (trimmed === "") {
    return text;
  }
  const lead = text.slice(0, text.length - text.trimStart().length);
  const trail = text.slice(text.trimEnd().length);
  const run = emphasizeRun(trimmed, marks, SPANNED_EDGES);
  return `${lead}${inlineTextSpanTag(attributes)}${run}${INLINE_TEXT_SPAN_CLOSE_TAG}${trail}`;
}

/**
 * The run's own size and colour, or null when it carries neither.
 * @param marks - The formatting marks on the run
 * @returns The styling attributes, or null
 */
function textStyleAttributesOf(
  marks: EditorMark[],
): InlineTextSpanAttributes | null {
  const mark = marks.find(
    (candidate): candidate is EditorTextStyleMark =>
      candidate.type === TEXT_STYLE_MARK_NAME,
  );
  return mark?.attrs ?? null;
}

/**
 * Wraps a text run in its emphasis delimiters, keeping the run's outer
 * whitespace outside them (markdown ignores emphasis padded by spaces).
 * Underline has no markdown syntax, so it wraps the run in the `<u>` inline
 * HTML the renderer reads back. Bold and italic get the same treatment for
 * the one run markdown cannot express: a punctuation-edged run (a field
 * token's braces, a quoted word) pressed flush against a neighbor, where
 * CommonMark's flanking rules read the asterisk delimiters as literals. The
 * outermost mark becomes a `<b>`/`<i>` tag, whose own punctuation then makes
 * any inner delimiter parse.
 * @param text - The already-escaped text run
 * @param marks - The formatting marks on the run
 * @param flush - Whether the run's neighbors touch it with no whitespace
 * @returns The emphasized markdown
 */
function emphasizeRun(
  text: string,
  marks: EditorMark[],
  flush: FlushEdges,
): string {
  const bold = marks.some((mark) => mark.type === "bold") ? "**" : "";
  const italic = marks.some((mark) => mark.type === "italic") ? "*" : "";
  const underlined = marks.some((mark) => mark.type === "underline");
  const delimiter = `${bold}${italic}`;
  if ((delimiter === "" && !underlined) || text.trim() === "") {
    return text;
  }
  const lead = text.slice(0, text.length - text.trimStart().length);
  const trail = text.slice(text.trimEnd().length);
  const trimmed = text.trim();
  if (underlined) {
    return `${lead}<u>${delimiter}${trimmed}${delimiter}</u>${trail}`;
  }
  const openerBreaks =
    flush.before && lead === "" && !/^[\p{L}\p{N}]/u.test(trimmed);
  const closerBreaks =
    flush.after && trail === "" && !/[\p{L}\p{N}]$/u.test(trimmed);
  if (delimiter === "" || (!openerBreaks && !closerBreaks)) {
    return `${lead}${delimiter}${trimmed}${delimiter}${trail}`;
  }
  const inner = bold !== "" && italic !== "" ? `*${trimmed}*` : trimmed;
  const tag = bold !== "" ? "b" : "i";
  return `${lead}<${tag}>${inner}</${tag}>${trail}`;
}

/**
 * Serializes a committed list, one line per item.
 * @param list - The committed list block
 * @returns The markdown for the list
 */
function listToMarkdown(list: EditorList): string {
  return list.content
    .map((item, index) =>
      listItemToMarkdown(
        item,
        list.type === "bulletList"
          ? "- "
          : `${index + (list.attrs?.start ?? 1)}. `,
      ),
    )
    .join(list.attrs?.tight === false ? "\n\n" : "\n");
}

/**
 * Serializes one list item, indenting its continuation lines to the marker
 * width so nested blocks stay inside the item.
 * @param item - The committed list item
 * @param marker - The item's list marker, including its trailing space
 * @returns The markdown for the item
 */
function listItemToMarkdown(item: EditorListItem, marker: string): string {
  const indent = " ".repeat(marker.length);
  return itemBlocksToMarkdown(item.content)
    .split("\n")
    .map((line, index) => {
      if (index === 0) {
        return `${marker}${line}`;
      }
      return line === "" ? "" : `${indent}${line}`;
    })
    .join("\n");
}

/**
 * Serializes the blocks inside a list item, keeping a nested list tight
 * against the line above it so the list does not render loose.
 * @param blocks - The item's blocks
 * @returns The markdown for the item body
 */
function itemBlocksToMarkdown(blocks: EditorBlockNode[]): string {
  return blocks.reduce((markdown, block, index) => {
    const text = blockToMarkdown(block);
    if (index === 0) {
      return text;
    }
    const gap =
      block.type === "bulletList" || block.type === "orderedList"
        ? "\n"
        : "\n\n";
    return `${markdown}${gap}${text}`;
  }, "");
}

/**
 * Narrows an unknown value to a plain record.
 * @param value - The value to test
 * @returns True when the value is a non-null object
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/**
 * Reads an unknown value as an array, defaulting to empty.
 * @param value - The value to read
 * @returns The array, or [] when the value isn't one
 */
function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/**
 * Maps every entry through a parser, failing the whole collection when any
 * entry fails.
 * @param values - The raw entries
 * @param parse - The per-entry parser
 * @returns The parsed entries, or null when any entry fails
 */
function collectAll<T>(
  values: unknown[],
  parse: (value: unknown) => T | null,
): T[] | null {
  const parsed = values.map(parse);
  return parsed.every((value): value is T => value !== null) ? parsed : null;
}

/**
 * Parses one raw block node from the editor JSON.
 * @param value - The raw node
 * @returns The typed block, or null when it doesn't conform
 */
function parseBlockJson(value: unknown): EditorBlockNode | null {
  if (!isRecord(value)) {
    return null;
  }
  if (value.type === "horizontalRule") {
    return { type: "horizontalRule" };
  }
  if (value.type === "paragraph" || value.type === "heading") {
    return parseTextBlockJson(value, value.type);
  }
  if (value.type === "bulletList" || value.type === "orderedList") {
    const content = collectAll(asArray(value.content), parseListItemJson);
    return content
      ? {
          type: value.type,
          ...(isRecord(value.attrs)
            ? {
                attrs: {
                  ...(value.attrs.tight === false ? { tight: false } : {}),
                  ...(value.type === "orderedList" &&
                  typeof value.attrs.start === "number"
                    ? { start: value.attrs.start }
                    : {}),
                },
              }
            : {}),
          content,
        }
      : null;
  }
  if (value.type === "blockquote") {
    const content = collectAll(asArray(value.content), parseBlockJson);
    if (!content) {
      return null;
    }
    return {
      type: "blockquote",
      content: content.length > 0 ? content : [{ type: "paragraph" }],
    };
  }
  return null;
}

/**
 * Parses a paragraph or heading node from the editor JSON.
 * @param value - The raw node record
 * @param type - Which of the two textblock types it is
 * @returns The typed block, or null when its content doesn't conform
 */
function parseTextBlockJson(
  value: Record<string, unknown>,
  type: "paragraph" | "heading",
): EditorBlockNode | null {
  const content = collectAll(asArray(value.content), parseInlineJson);
  if (!content) {
    return null;
  }
  if (type === "paragraph") {
    return content.length > 0
      ? { type: "paragraph", content }
      : { type: "paragraph" };
  }
  const attrs = isRecord(value.attrs) ? value.attrs : {};
  const level = isHeadingLevel(attrs.level) ? attrs.level : HEADING_LEVELS[0];
  return content.length > 0
    ? { type: "heading", attrs: { level }, content }
    : { type: "heading", attrs: { level } };
}

/**
 * Parses one raw list-item node from the editor JSON.
 * @param value - The raw node
 * @returns The typed list item, or null when it doesn't conform
 */
function parseListItemJson(value: unknown): EditorListItem | null {
  if (!isRecord(value) || value.type !== "listItem") {
    return null;
  }
  const content = collectAll(asArray(value.content), parseBlockJson);
  if (!content) {
    return null;
  }
  return {
    type: "listItem",
    content: content.length > 0 ? content : [{ type: "paragraph" }],
  };
}

/**
 * Parses one raw inline node from the editor JSON.
 * @param value - The raw node
 * @returns The typed inline node, or null when it doesn't conform
 */
function parseInlineJson(value: unknown): EditorInlineNode | null {
  if (!isRecord(value)) {
    return null;
  }
  if (value.type === "hardBreak") {
    return { type: "hardBreak" };
  }
  if (value.type === "text") {
    if (typeof value.text !== "string") {
      return null;
    }
    const marks = parseMarksJson(value.marks);
    return marks.length > 0
      ? { type: "text", text: value.text, marks }
      : { type: "text", text: value.text };
  }
  if (value.type === FIELD_CHIP_NODE_NAME) {
    const raw = isRecord(value.attrs) ? value.attrs : {};
    const attrs = {
      source: typeof raw.source === "string" ? raw.source : "",
      label: typeof raw.label === "string" ? raw.label : "",
    };
    const marks = parseMarksJson(value.marks);
    return marks.length > 0
      ? { type: FIELD_CHIP_NODE_NAME, attrs, marks }
      : { type: FIELD_CHIP_NODE_NAME, attrs };
  }
  return null;
}

/**
 * Parses the recognized formatting marks from a raw mark list, ignoring
 * anything else.
 * @param value - The raw marks value
 * @returns The typed marks
 */
function parseMarksJson(value: unknown): EditorMark[] {
  return asArray(value).flatMap((mark): EditorMark[] => {
    if (!isRecord(mark)) {
      return [];
    }
    if (mark.type === TEXT_STYLE_MARK_NAME) {
      const attrs = readInlineTextSpanAttributes(mark.attrs);
      return attrs === null ? [] : [{ type: TEXT_STYLE_MARK_NAME, attrs }];
    }
    return mark.type === "bold" ||
      mark.type === "italic" ||
      mark.type === "underline"
      ? [{ type: mark.type }]
      : [];
  });
}
